import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { APISetting } from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import { gsap } from "gsap";

import { IReelData } from "../../../../abstracts/elements/AbstractReel";
import { AbstractSymbol } from "../../../../abstracts/elements/AbstractSymbol";
import { EventsManager, FlowEvents } from "../../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../../abstracts/flow/AbstractGameMode";
import { getRandomElement } from "../../../../abstracts/utils/JSUtils";
import { playSound } from "../../../../abstracts/utils/SoundUtils";
import { Apocalypse } from "../../../Apocalypse";
import { ApocalypseSoundNames } from "../../../assetsLoader/ApocalypseAssetsNames";
import {
    fsConfig,
    fsStage1ReelSet,
    fsStage2ReelSet,
    reelsConfig,
    symbolConfig,
} from "../../../config/ApocalypseConfig";
import {
    ApocalypseHighSymbolNames,
    ApocalypseLowSymbolNames,
    ApocalypseSpecialSymbolNames,
} from "../../../types/ApocalypseEnums";
import ApocalypseReel from "../normal/ApocalypseReel";
import { ApocalypseBlankSymbol } from "../symbols/ApocalypseBlankSymbol";
import { ApocalypseSymbol } from "../symbols/ApocalypseSymbol";
import { getSymbolFromPool, returnSymbolToPool } from "../symbols/ApocalypseSymbolsPool";

export class ApocalypseFsReel extends ApocalypseReel {
    public isPersistent: boolean = false;
    public teasingSymbolName: string = ``;

    constructor(
        reelId: number,
        symbols: string[],
        gameMode: AbstractGameMode = AbstractGameMode.FREESPIN
    ) {
        super(reelId, symbols, gameMode);

        this.name = `fsReel` + reelId.toString();

        if (
            symbols[0] !== ApocalypseSpecialSymbolNames.BLANK &&
            symbols[0] !== ApocalypseSpecialSymbolNames.DUMP
        ) {
            this.isLocked = true;
        }

        EventsManager.on(FlowEvents.FREESPIN_STAGES, (isFirstStage: boolean) => {
            this.fsReelSet =
                !isFirstStage || (isFirstStage && Apocalypse.model.data.freespinsLeft === 0)
                    ? fsStage2ReelSet
                    : fsStage1ReelSet;

            if (isFirstStage) {
                if (
                    this.reelId < reelsConfig.reelsNumber ||
                    this.reelId >= reelsConfig.reelsNumber * (reelsConfig.fsRowsNumber - 1)
                ) {
                    this.isLocked = true;
                }
            } else {
                if (
                    this.reelId < reelsConfig.reelsNumber ||
                    this.reelId >= reelsConfig.reelsNumber * (reelsConfig.fsRowsNumber - 1)
                ) {
                    this.isLocked = false;
                } else {
                    this.isLocked = true;
                }
            }
        });
    }

    protected override generateInitialSymbols(symbols: string[]) {
        this.fsReelSet = fsStage1ReelSet;

        super.generateInitialSymbols(symbols);
    }

    protected get stepSize(): number {
        return fsConfig.stepSize!;
    }

    protected override get reelSet(): string[] {
        return this.fsReelSet;
    }

    protected getRandomSymbols(count: number): string[] {
        const result: string[] = [];
        for (let i: number = 0; i < count; i++) {
            if (this.reelId <= 5 || this.reelId >= 30) {
                result.push(ApocalypseSpecialSymbolNames.BLANK);
            } else {
                result.push(this.getRandomSymbolInReelSet());
            }
        }
        return result;
    }

    protected createNewSymbol(name: string, position: number): ApocalypseSymbol {
        const symbol = super.createNewSymbol(name, position) as ApocalypseSymbol;

        if (Object.values(ApocalypseLowSymbolNames).includes(name as ApocalypseLowSymbolNames)) {
            symbol.showFsText();
        }

        if (symbol instanceof ApocalypseBlankSymbol && (this.reelId <= 5 || this.reelId >= 30)) {
            symbol.hasSpecialTexture = true;
        }

        return symbol;
    }

    protected override getVisibleSymbolsCount(): number {
        this.visibleSymbolsCount = 1;

        return this.visibleSymbolsCount;
    }

    public override startNormalBetAnimation(): gsap.core.Timeline {
        if (this.isLocked) {
            return gsap.timeline();
        }

        return super.startNormalBetAnimation();
    }

    protected override getEaseFactor(): number {
        this.customStepDuration = SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)
            ? fsConfig.stepDuration! - 0.03
            : fsConfig.stepDuration!;

        return SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 1 : 0.5;
    }

    protected getStopDurationFactor() {
        // ! is fast spin active fa event
        return fsConfig.stepDuration!;
    }

    public override stopNormalBetAnimation(
        data: IReelData,
        onReelComplete: Function,
        onLandStart: Function = () => {},
        hasTeasing: boolean = false
    ): gsap.core.Timeline {
        if (this.isLocked) {
            onLandStart(this.reelId);
            return gsap.timeline({
                smoothChildTiming: true,
                autoRemoveChildren: true,
                onComplete: () => {
                    onReelComplete(this.reelId);
                },
            });
        }

        if (hasTeasing) {
            this.teasingSymbolName = data.stopSymbolNames![0];
            data.stopSymbolNames![0] = getRandomElement(fsStage2ReelSet);
        }

        return super.stopNormalBetAnimation(data, onReelComplete, onLandStart, hasTeasing);
    }

    protected bounce(
        onReelComplete: Function = () => {},
        onLandStart: Function = () => {},
        hasTeasing: boolean = false
    ): gsap.core.Timeline {
        const tl = gsap.timeline();

        let currentSymbolsClone = [...this.currentSymbols];
        const currentSymbolsWithTease: ApocalypseSymbol[] = [];
        if (hasTeasing) {
            currentSymbolsClone = [];
            currentSymbolsClone.push(this.currentSymbols[0]);

            const teasingSymbol = getSymbolFromPool(getRandomElement(fsStage2ReelSet));
            if (teasingSymbol instanceof ApocalypseBlankSymbol) {
                teasingSymbol.hasSpecialTexture = true;
            }
            // teasingSymbol.name = `teasingSymbol`;
            teasingSymbol.y = this.currentSymbols[0].y;
            teasingSymbol.positionInArray = 1;
            teasingSymbol.reelPosition = -1;
            this.symbolsContainer.addChildAt(teasingSymbol, 0);

            const finalSymbol = getSymbolFromPool(this.teasingSymbolName);
            if (finalSymbol instanceof ApocalypseBlankSymbol) {
                finalSymbol.hasSpecialTexture = true;
            }
            // finalSymbol.name = `finalSymbol`;
            finalSymbol.y = teasingSymbol.y - symbolConfig.symbolHeight;
            finalSymbol.positionInArray = 2;
            finalSymbol.reelPosition = -2;
            this.symbolsContainer.addChildAt(finalSymbol, 0);
            currentSymbolsClone.push(finalSymbol);
            currentSymbolsClone.push(teasingSymbol);

            this.currentSymbols[0].y = finalSymbol.y - symbolConfig.symbolHeight;
            this.currentSymbols[0].reelPosition = -3;
            // this.currentSymbols[0].name = `currentSymbol0`;
            this.symbolsContainer.addChildAt(this.currentSymbols[0], 0);

            this.currentSymbols[1].positionInArray = 3;
            this.currentSymbols[1].reelPosition = 0;
            currentSymbolsClone.push(this.currentSymbols[1]);

            this.currentSymbols[2].positionInArray = 4;
            this.currentSymbols[2].reelPosition = 1;
            currentSymbolsClone.push(this.currentSymbols[2]);

            this.currentSymbols = currentSymbolsClone;

            currentSymbolsWithTease.push(this.currentSymbols[0] as ApocalypseSymbol);
            currentSymbolsWithTease.push(finalSymbol as ApocalypseSymbol);
            currentSymbolsWithTease.push(teasingSymbol as ApocalypseSymbol);

            this.stopSymbols = currentSymbolsWithTease;
        }

        const overshoot = gsap.to(this.animatedStepData, {
            duration: hasTeasing ? fsConfig.reelTeasingDuration! * 0.4 : 0.06,
            y: hasTeasing ? 50 : this.stepSize / 16,
            ease: hasTeasing ? `circ.out` : `linear`,
            onUpdate: () => this.updatePosition(),
            onStart: () => {
                onLandStart(this.reelId);

                // playSound({
                //     name: reelLandSounds[this.reelId],
                //     stereo: davidPanningReelStop[this.reelId],
                // });
            },
        });

        const back = gsap.to(this.animatedStepData, {
            duration: hasTeasing ? fsConfig.reelTeasingDuration! * 0.6 : 0.1,
            y: hasTeasing ? 212 : 0,
            ease: hasTeasing ? `circ.in` : `power1.in`,
            onUpdate: () => this.updatePosition(),
            onStart: () => {
                this.handleSoundsOnLand();
            },
            onComplete: () => {
                onReelComplete(this.reelId);

                if (hasTeasing) {
                    currentSymbolsWithTease[0].positionInArray = 0;
                    currentSymbolsWithTease[0].reelPosition = -1;

                    currentSymbolsWithTease[1].positionInArray = 1;
                    currentSymbolsWithTease[1].reelPosition = 0;

                    currentSymbolsWithTease[2].positionInArray = 2;
                    currentSymbolsWithTease[2].reelPosition = 1;

                    gsap.to(currentSymbolsWithTease[2], {
                        alpha: 0,
                        duration: 0.1,
                    });

                    const prevCurrentSymbols = [...this.currentSymbols];

                    prevCurrentSymbols.forEach((symbol) => {
                        if (!currentSymbolsWithTease.includes(symbol as ApocalypseSymbol)) {
                            returnSymbolToPool(symbol);
                        }
                    });

                    this.currentSymbols = currentSymbolsWithTease;

                    this.animatedStepData.y = 0;
                    this.updatePosition();
                }
            },
        });

        tl.add(overshoot);
        tl.add(back);

        tl.timeScale(SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? 2 : 1);

        return tl;
    }

    public getAllVisibleStopSymbols(): AbstractSymbol[] {
        if (this.stopSymbols.length > 0 && !this.isLocked) {
            return [this.stopSymbols[1]];
        }

        return [this.currentSymbols[1]];
    }

    public getSwitchSymbolsAnimation(
        oldSymbol: ApocalypseSymbol,
        newSymbol: ApocalypseSymbol
    ): gsap.core.Timeline {
        newSymbol.reelPosition = oldSymbol.reelPosition;
        newSymbol.position = oldSymbol.position;
        newSymbol.showFsText();
        newSymbol.alpha = 0;

        if (this.currentSymbols.indexOf(oldSymbol) >= 0) {
            this.currentSymbols[this.currentSymbols.indexOf(oldSymbol)] = newSymbol;
        }

        if (this.stopSymbols.indexOf(oldSymbol) >= 0) {
            this.stopSymbols[this.currentSymbols.indexOf(oldSymbol)] = newSymbol;
        }

        const timeline = gsap.timeline();

        this.symbolsContainer.addChildAt(newSymbol, 0);

        const showNewSymbol = gsap.to(newSymbol, {
            alpha: 1,
            duration: 0.4,
            onStart: () => {},
            onComplete: () => {
                returnSymbolToPool(oldSymbol);
            },
        });
        timeline.add(showNewSymbol, 0);

        return timeline;
    }

    public updateCurrentSymbol(newSymbol: ApocalypseSymbol, destroyOldSymbol: boolean): void {
        const oldSymbol = this.currentSymbols[1];

        newSymbol.position = oldSymbol.position;
        newSymbol.positionInArray = oldSymbol.positionInArray;
        newSymbol.reelPosition = oldSymbol.reelPosition;

        this.symbolsContainer.addChild(newSymbol);
        this.currentSymbols[1] = newSymbol;

        if (this.stopSymbols.indexOf(oldSymbol) >= 0) {
            this.stopSymbols[1] = newSymbol;
        }

        if (destroyOldSymbol) {
            returnSymbolToPool(oldSymbol);
        }
    }

    public getUpgradeSymbolAnimation(newSymbol: ApocalypseSymbol): gsap.core.Timeline {
        const oldSymbol = this.currentSymbols[1];
        const topSymbol = this.currentSymbols[0];

        newSymbol.showFsText();
        newSymbol.y = -symbolConfig.symbolHeight;

        newSymbol.alpha = 0;
        this.symbolsContainer.addChild(newSymbol);

        topSymbol.y = newSymbol.y - symbolConfig.symbolHeight;

        this.currentSymbols[1] = newSymbol;

        if (this.stopSymbols.indexOf(oldSymbol) === 1) {
            this.stopSymbols[1] = newSymbol;
        }

        const timeline = gsap.timeline({ delay: 0.14 });

        const showNewSymbol = gsap.fromTo(
            newSymbol,
            {
                y: -symbolConfig.symbolHeight,
            },
            {
                y: 0,
                duration: 0.65,
                ease: `back.out(1.4)`,
                onStart: () => {
                    newSymbol.alpha = 1;
                },
                onUpdate: () => {
                    topSymbol.y = newSymbol.y - symbolConfig.symbolHeight;
                },
            }
        );
        timeline.add(showNewSymbol, 0);

        const hideOldSymbol = gsap.to(oldSymbol, {
            y: symbolConfig.symbolHeight,
            duration: showNewSymbol.totalDuration(),
            ease: `back.out(1.6)`,

            onComplete: () => {
                returnSymbolToPool(oldSymbol);
            },
        });
        timeline.add(hideOldSymbol, 0);

        newSymbol.y = 0;

        return timeline;
    }

    public override reset() {
        this.isLocked = false;
        this.isPersistent = false;

        super.reset();
    }
}
